"use client";

import type { TutorAvatarHandle } from "@/components/VoiceAgent";
import {
  type CSSProperties,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { type Silhouette, facePolygon, outlinePolygon, traceSilhouette } from "./silhouette";

/**
 * The conversation area's size, today's header circle position and the composer
 * band, as CSS variables on the area. Measured rather than container-queried: a
 * container would become the containing block of the fixed fullscreen avatar.
 */
export function useTutorStageGeometry(
  areaRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  deps: unknown[]
) {
  useLayoutEffect(() => {
    const area = areaRef.current;
    if (!enabled || !area) return;
    const update = () => {
      const areaBox = area.getBoundingClientRect();
      area.style.setProperty("--area-w", `${areaBox.width}px`);
      area.style.setProperty("--area-h", `${areaBox.height}px`);
      const composer = area.querySelector<HTMLElement>("[data-transcript-composer]");
      area.style.setProperty("--composer-h", `${composer?.offsetHeight ?? 0}px`);
      // SessionHeader reserves the circle as the first item of its right group.
      // Layout offsets, not client rects: the header slides in with a transform,
      // which a resize observer would not report.
      const group = area.querySelector<HTMLElement>("[data-session-toolbar] > :last-child");
      if (!group) return;
      let x = 0;
      let y = 0;
      for (
        let node: HTMLElement | null = group;
        node && node !== area;
        node = node.offsetParent as HTMLElement | null
      ) {
        x += node.offsetLeft;
        y += node.offsetTop;
      }
      area.style.setProperty("--thumb-x", `${x}px`);
      area.style.setProperty("--thumb-y", `${y + (group.offsetHeight - 96) / 2}px`);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(area);
    for (const selector of [
      "[data-transcript-header]",
      "[data-tutor-stage-header]",
      "[data-transcript-composer]",
    ]) {
      const node = area.querySelector(selector);
      if (node) observer.observe(node);
    }
    return () => observer.disconnect();
    // The caller lists what changes the header and composer it should observe.
  }, [areaRef, enabled, ...deps]);
}

/** Her box is sized so her figure fills about 80% of its width. */
const FIGURE_SHARE = 0.8;
const MIN_ASPECT = 0.9;
const MAX_ASPECT = 1.6;
export const DEFAULT_AVATAR_ASPECT = 1.4;

export interface AvatarFraming {
  aspect: number;
  silhouette: Silhouette | null;
}

async function traceFrame(handle: TutorAvatarHandle): Promise<Silhouette | null> {
  const blob = await handle.exportAvatarFrame();
  if (!blob) return null;
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(bitmap, 0, 0);
    const { data } = context.getImageData(0, 0, bitmap.width, bitmap.height);
    return traceSilhouette(
      (x, y) => data[(y * bitmap.width + x) * 4 + 3],
      bitmap.width,
      bitmap.height
    );
  } finally {
    bitmap.close();
  }
}

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const FRAMING_KEY_PREFIX = "chickytutor:avatar-framing:v1:";
const LAST_FRAMING_KEY = `${FRAMING_KEY_PREFIX}last`;

function readStoredFraming(key: string): AvatarFraming | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AvatarFraming;
    if (typeof parsed?.aspect !== "number" || !parsed.silhouette?.outline?.length) return null;
    return parsed;
  } catch {
    return null;
  }
}

function storeFraming(avatarId: string, framing: AvatarFraming) {
  try {
    const value = JSON.stringify(framing);
    window.localStorage.setItem(`${FRAMING_KEY_PREFIX}${avatarId}`, value);
    window.localStorage.setItem(LAST_FRAMING_KEY, value);
  } catch {
    // The avatar is simply measured again next session.
  }
}

function initialFraming(): AvatarFraming {
  // Usually the same avatar as last time: start from her, not a generic shape.
  if (typeof window !== "undefined") {
    const last = readStoredFraming(LAST_FRAMING_KEY);
    if (last) return last;
  }
  return { aspect: DEFAULT_AVATAR_ASPECT, silhouette: null };
}

/**
 * Her framing for the layout. Each avatar is measured once per browser and
 * remembered, so later sessions lay out correctly from the first frame. A new
 * avatar is traced from her first idle frame, which AvatarKit draws before the
 * session connects, while she is still invisible: first to size her box to her
 * figure (avatars differ: about 0.8 to 1.2 times the box height wide), then, in
 * that box, to place the text wrap, her face-safe area, her shoulder line and
 * her controls. `framed` tells the stage she can be shown: her framing is final.
 */
export function useAvatarFraming(handle: TutorAvatarHandle | null, enabled: boolean) {
  const [framing, setFraming] = useState<AvatarFraming>(initialFraming);
  const [framedAvatarId, setFramedAvatarId] = useState<string | null>(null);
  const avatarId = handle?.avatarId || null;
  const viewReady = Boolean(handle?.isViewReady);
  const handleRef = useRef(handle);
  handleRef.current = handle;
  const aspectRef = useRef(framing.aspect);
  aspectRef.current = framing.aspect;

  // A known avatar: her remembered framing applies before the next paint.
  useLayoutEffect(() => {
    if (!avatarId) return;
    const stored = readStoredFraming(`${FRAMING_KEY_PREFIX}${avatarId}`);
    if (!stored) return;
    setFraming(stored);
    setFramedAvatarId(avatarId);
  }, [avatarId]);

  // A new avatar: measure her first idle frame, then remember her.
  useEffect(() => {
    if (!enabled || !avatarId || !viewReady || framedAvatarId === avatarId) return;
    let cancelled = false;
    const trace = () => {
      const current = handleRef.current;
      return current ? traceFrame(current).catch(() => null) : Promise.resolve(null);
    };

    const run = async () => {
      // The first frames can still be loading; try for a few seconds.
      let first: Silhouette | null = null;
      for (let attempt = 0; attempt < 12 && !first; attempt += 1) {
        await wait(attempt === 0 ? 150 : 500);
        if (cancelled) return;
        first = await trace();
      }
      if (cancelled) return;
      if (!first) {
        // Show her with the current framing rather than not at all.
        setFramedAvatarId(avatarId);
        return;
      }

      const traced = aspectRef.current;
      const figureWidth = ((first.bounds.right - first.bounds.left) / 100) * traced;
      const aspect = Math.min(MAX_ASPECT, Math.max(MIN_ASPECT, figureWidth / FIGURE_SHARE));
      let final: AvatarFraming = { aspect: traced, silhouette: first };
      if (Math.abs(aspect - traced) >= 0.02) {
        setFraming((previous) => ({ ...previous, aspect }));
        // Let the box resize and the canvas redraw at the new shape.
        await wait(600);
        if (cancelled) return;
        const second = await trace();
        if (cancelled) return;
        final = { aspect, silhouette: second ?? null };
      }
      setFraming(final);
      if (final.silhouette) storeFraming(avatarId, final);
      setFramedAvatarId(avatarId);
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [avatarId, enabled, framedAvatarId, viewReady]);

  return { framing, framed: Boolean(avatarId) && framedAvatarId === avatarId };
}

/** The traced framing as the stage's CSS variables. */
export function framingStyle(framing: AvatarFraming): CSSProperties {
  const { aspect, silhouette } = framing;
  const style: Record<string, string> = { "--avatar-aspect": String(aspect) };
  if (silhouette) {
    style["--fig-l"] = String(silhouette.bounds.left / 100);
    style["--fig-r"] = String(silhouette.bounds.right / 100);
    style["--fig-top"] = String(silhouette.bounds.top / 100);
    style["--face-end"] = String(silhouette.faceEnd / 100);
    style["--face-l"] = String(silhouette.faceLeft / 100);
    style["--tutor-outline"] = outlinePolygon(silhouette);
    style["--tutor-face"] = facePolygon(silhouette);
  }
  return style as CSSProperties;
}
